import React, { useState, useEffect, useRef } from 'react';
import { 
  Search, 
  User, 
  Building, 
  X,
  Loader2
} from 'lucide-react';
import { searchAll } from '../services/adminService';
import { AdminUser, AdminOrganization } from '../types/admin.types';

interface UnifiedSearchProps {
  onSelectPerson: (user: AdminUser) => void;
  onSelectOrganization: (org: AdminOrganization) => void;
  placeholder?: string;
}

const UnifiedSearch: React.FC<UnifiedSearchProps> = ({
  onSelectPerson,
  onSelectOrganization,
  placeholder = "Search by name, organization, or any email address..."
}) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [isSearching, setIsSearching] = useState(false);
  const [showResults, setShowResults] = useState(false);
  const [searchResults, setSearchResults] = useState<{
    users: AdminUser[];
    organizations: AdminOrganization[];
  }>({ users: [], organizations: [] });
  
  const searchRef = useRef<HTMLDivElement>(null);
  const searchTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (searchRef.current && !searchRef.current.contains(event.target as Node)) {
        setShowResults(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  useEffect(() => {
    if (searchTerm.length >= 2) {
      // Debounce search
      if (searchTimeoutRef.current) {
        clearTimeout(searchTimeoutRef.current);
      }
      
      searchTimeoutRef.current = setTimeout(() => {
        performSearch(searchTerm);
      }, 300);
    } else {
      setSearchResults({ users: [], organizations: [] });
      setShowResults(false);
    }

    return () => {
      if (searchTimeoutRef.current) {
        clearTimeout(searchTimeoutRef.current);
      }
    };
  }, [searchTerm]);

  const performSearch = async (term: string) => {
    setIsSearching(true);
    try {
      const results = await searchAll(term);
      setSearchResults(results);
      setShowResults(true);
    } catch (error) {
    }
    setIsSearching(false);
  };

  const handleSelectPerson = (user: AdminUser) => {
    onSelectPerson(user);
    setSearchTerm('');
    setShowResults(false);
  };

  const handleSelectOrganization = (org: AdminOrganization) => {
    onSelectOrganization(org);
    setSearchTerm('');
    setShowResults(false);
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Escape') {
      setShowResults(false);
      setSearchTerm('');
    }
  };

  const hasResults = searchResults.users.length > 0 || searchResults.organizations.length > 0;

  return (
    <div ref={searchRef} className="relative">
      <div className="relative">
        <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
          {isSearching ? (
            <Loader2 className="h-5 w-5 text-gray-400 animate-spin" />
          ) : (
            <Search className="h-5 w-5 text-gray-400" />
          )}
        </div>
        <input
          type="text"
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          onKeyDown={handleKeyDown}
          onFocus={() => searchTerm.length >= 2 && hasResults && setShowResults(true)}
          placeholder={placeholder}
          className="block w-full pl-10 pr-10 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-sky-500 focus:border-sky-500 text-sm"
        />
        {searchTerm && (
          <button
            onClick={() => {
              setSearchTerm('');
              setShowResults(false);
            }}
            className="absolute inset-y-0 right-0 pr-3 flex items-center"
          >
            <X className="h-4 w-4 text-gray-400 hover:text-gray-600" />
          </button>
        )}
      </div>

      {/* Search Results Dropdown */}
      {showResults && hasResults && (
        <div className="absolute z-50 mt-2 w-full bg-white rounded-lg shadow-lg border border-gray-200 max-h-96 overflow-auto">
          {/* Organizations Results */}
          {searchResults.organizations.length > 0 && (
            <div>
              <div className="px-4 py-2 bg-gray-50 border-b border-gray-200">
                <h3 className="text-xs font-semibold text-gray-500 uppercase tracking-wider">
                  Organizations ({searchResults.organizations.length})
                </h3>
              </div>
              <div className="py-1">
                {searchResults.organizations.map((org) => (
                  <button
                    key={org.name}
                    onClick={() => handleSelectOrganization(org)}
                    className="w-full px-4 py-2 hover:bg-gray-50 flex items-start gap-3 text-left transition-colors"
                  >
                    <Building className="h-5 w-5 text-gray-400 mt-0.5" />
                    <div className="flex-1 min-w-0">
                      <div className="font-medium text-gray-900">{org.name}</div>
                      {org.industry && (
                        <div className="text-sm text-gray-500">{org.industry}</div>
                      )}
                      <div className="flex items-center gap-3 mt-1 text-xs text-gray-500">
                        <span>{org.totalAssessments} assessment{org.totalAssessments !== 1 ? 's' : ''}</span>
                        {org.latestTASScore && (
                          <span>TAS: {Math.round(org.latestTASScore)}</span>
                        )}
                        <span>{org.assessors.length} assessor{org.assessors.length !== 1 ? 's' : ''}</span>
                      </div>
                    </div>
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* People Results */}
          {searchResults.users.length > 0 && (
            <div>
              <div className="px-4 py-2 bg-gray-50 border-b border-gray-200">
                <h3 className="text-xs font-semibold text-gray-500 uppercase tracking-wider">
                  People ({searchResults.users.length})
                </h3>
              </div>
              <div className="py-1">
                {searchResults.users.map((user) => (
                  <button
                    key={user.userId}
                    onClick={() => handleSelectPerson(user)}
                    className="w-full px-4 py-2 hover:bg-gray-50 flex items-start gap-3 text-left transition-colors"
                  >
                    <User className="h-5 w-5 text-gray-400 mt-0.5" />
                    <div className="flex-1 min-w-0">
                      <div className="font-medium text-gray-900">
                        {user.name || 'Unknown Name'}
                      </div>
                      <div className="text-sm text-gray-500">{user.email}</div>
                      {user.organizationName && (
                        <div className="text-xs text-gray-400 mt-0.5">
                          {user.organizationName}
                        </div>
                      )}
                      <div className="flex items-center gap-2 mt-1">
                        <span className="text-xs text-gray-500">
                          {user.totalSessions} session{user.totalSessions !== 1 ? 's' : ''}
                        </span>
                        {user.isRooster && (
                          <span className="bg-purple-100 text-purple-700 text-xs px-1.5 py-0.5 rounded">
                            Rooster
                          </span>
                        )}
                      </div>
                    </div>
                  </button>
                ))}
              </div>
            </div>
          )}
        </div>
      )}

      {/* No Results */}
      {showResults && !hasResults && searchTerm.length >= 2 && !isSearching && (
        <div className="absolute z-50 mt-2 w-full bg-white rounded-lg shadow-lg border border-gray-200 p-4">
          <p className="text-sm text-gray-500 text-center">
            No results found for "{searchTerm}"
          </p>
        </div>
      )}
    </div>
  );
};

export default UnifiedSearch;